import type { FallbackProps } from '@gik/core/api/BaseAPIConfig';
import { withTitle } from '@gik/core/hocs/withTitle';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import type { IHomePageProps } from '@gik/landing-pages/pages/HomePage';
import type { ILandingPageProps } from '@gik/landing-pages/pages/LandingPage';
import { LandingPage } from '@gik/landing-pages/pages/LandingPage';
import type { PageLayoutProps } from '@/components/layout/PageLayout';
import { PageLayout } from '@/components/layout/PageLayout';
import { SWRConfig } from 'swr';

export default function LandingPageTemplateComp({ fallback }: FallbackProps<IHomePageProps>) {
  return withTitle(
    'Care Calendar',
    <SWRConfig value={{ fallback }}>
      <LandingPage {...landingPageProps} url="/set-up-meal-train" className={'gik-set-up-meal-train'} />
    </SWRConfig>
  );
}

LandingPageTemplateComp.getLayout = function getLayout(page, layoutProps: PageLayoutProps) {
  return (
    <PageLayout {...layoutProps} key={layoutProps.key}>
      {page}
    </PageLayout>
  );
};

// FIXME: Replace this strategy with i18n library instead
// @ts-ignore
const landingPageProps: ILandingPageProps = {
  heroSection: {
    hero: {
      title: 'Meal Trains & More',
      content:
        'With Give InKind, you can organize a meal train and coordinate unlimited types of help all in one place—completely free.',
    },
    backgroundImage: `${getAssetsUrl()}landing-pages/headers/Hero-mealTrain-mobile@2x.png`,
    backgroundImageMd: `${getAssetsUrl()}landing-pages/headers/Hero-mealTrain-desktop@2x.png`,
    backgroundColor: '#f4ffff',
  },
  featureFocus: {
    feature: {
      title: 'Why Choose Give InKind?',
      subtitle:
        'Unlike traditional meal train platforms, Give InKind offers so much more than just meal coordination. Our all-in-one support platform makes it easy to:',
      icon: `${getAssetsUrl()}landing-pages/animations/Full-Phone.gif`,
      iconStyles: {
        maxWidth: 'unset',
        width: '190%',
        marginLeft: '-48%',
      },
      features: [
        <>
          <span className={'tw-font-bold'}>Effortless Meal Scheduling:</span> Organize a meal train with a Care Calendar
          where friends and family can sign up for meal deliveries.
        </>,
        <>
          <span className={'tw-font-bold'}>More Than Meals:</span> Add childcare, pet sitting, or errand alongside meal
          drop-offs for complete support.
        </>,
        <>
          <span className={'tw-font-bold'}>Gift Card Options:</span> No time to cook? Send gift cards for takeout or
          groceries directly through the calendar.
        </>,
        <>
          <span className={'tw-font-bold'}>Stay Connected:</span> Post updates and messages to keep everyone informed
          throughout the meal train.
        </>,
        <>
          <span className={'tw-font-bold'}>Always Free:</span> Setting up and managing a meal train on Give InKind is
          100% free.
        </>,
      ],
    },
  },
  ctaSection: {
    ctas: [
      {
        title: 'Customizable Calendar',
        content:
          'Give InKind’s Care Calendar makes it easy to organize help and coordinate support all in one place. Add unlimited help types—like meals, rides, childcare, and more—so others know exactly how to contribute.',
        icon: `${getAssetsUrl()}landing-pages/features/MT-Feature01.png`,
      },
      {
        title: 'Integrated Gift Cards',
        content:
          'Only Give InKind connects gift cards directly to the Care Calendar, making it easier than ever to support someone in need. Whether a friend can deliver a meal locally or a relative wants to help from across the country, they can sign up or send a gift card—all in one place.',
        icon: `${getAssetsUrl()}landing-pages/features/MT-Feature02.png`,
      },
      {
        title: 'And more - for free!',
        content:
          'With automatic reminders, one-click thank you notes, unlimited organizers, and more, Give InKind simplifies every step of coordinating care and keeps your community effortlessly connected.',
        icon: `${getAssetsUrl()}landing-pages/features/MT-Feature03.png`,
      },
    ],
  },
};
